/**
 * Application Routes
 *
 * Centralized routing configuration for the entire application.
 */

import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { useAuth } from "../contexts/AuthContext";

// Auth pages
import { LoginPage } from "../features/auth/pages/LoginPage";
import { RegisterPage } from "../features/auth/pages/RegisterPage";
import { VerifyTenantPage } from "../features/auth/pages/VerifyTenantPage";
import { RequestPasswordResetPage } from "../features/auth/pages/RequestPasswordResetPage";
import { ResetPasswordPage } from "../features/auth/pages/ResetPasswordPage";
import { RequestAccessPage } from "../features/auth/pages/RequestAccessPage";
import { CompleteRegistrationPage } from "../features/auth/pages/CompleteRegistrationPage";
import { SSOCallbackPage } from "../features/auth/pages/SSOCallbackPage";

// Protected pages
import { DashboardPage } from "../pages/DashboardPage";
import { UnauthorizedPage } from "../pages/UnauthorizedPage";
import { ProfilePage } from "../pages/ProfilePage";
import { SettingsPage } from "../pages/SettingsPage";
import { LauncherPage } from "../pages/LauncherPage";
import { LaunchPage } from "../pages/LaunchPage";
import { BillingPage } from "../pages/BillingPage";

// Feature pages
import { ApplicationsPage } from "../features/applications/pages/ApplicationsPage";
import { VolumesPage } from "../features/volumes/pages/VolumesPage";
import { DatabaseConnectionsPage } from "../features/database-connections/pages/DatabaseConnectionsPage";
import { SecretsPage } from "../features/secrets/pages/SecretsPage";
import { DocumentDatabasesPage } from "../features/document-databases/pages/DocumentDatabasesPage";
import { UsersPage } from "../features/users/pages/UsersPage";
import { AcceptInvitationPage } from "../features/users/pages/AcceptInvitationPage";
import { GroupsPage } from "../features/groups/pages/GroupsPage";
import { RolesPage } from "../features/roles/pages/RolesPage";

// Managed SQL Databases
import { ManagedSqlDatabasesPage } from "../features/managed-sql-databases/pages/ManagedSqlDatabasesPage";

// Data ELT feature pages
import { DataAgentsPage } from "../features/data-agents/pages/DataAgentsPage";
import { EltJobsPage } from "../features/elt-jobs/pages/EltJobsPage";
import { EltJobExecutionsPage } from "../features/job-executions/pages/EltJobExecutionsPage";

// Marketplace feature pages
import { MarketplacePage } from "../features/marketplace/pages/MarketplacePage";
import { ListingDetailPage } from "../features/marketplace/pages/ListingDetailPage";
import { InstalledAppsPage } from "../features/marketplace/pages/InstalledAppsPage";
import { PartnerDashboardPage } from "../features/marketplace/pages/PartnerDashboardPage";

// Support tickets
import { SupportTicketsPage } from "../features/support-tickets/pages/SupportTicketsPage";

// Inbox
import { InboxPage } from "../features/inbox";

// App Jobs
import { AppJobsPage } from "../features/app-jobs";

// Background Jobs
import { BackgroundJobsPage } from "../features/background-jobs";

// Service Accounts
import { ServiceAccountsPage } from "../features/service-accounts";

// SSO
import { SSOConfigPage } from "../features/sso";

// Audit Logs
import { AuditLogsPage } from "../features/audit-logs";

/**
 * Root redirect component
 * Redirects to dashboard if authenticated, otherwise to login
 */
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return null; // Or a loading spinner
  }

  return isAuthenticated ? <Navigate to="/app/dashboard" replace /> : <Navigate to="/login" replace />;
};

/**
 * Application router
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Public routes - Authentication */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/request-access" element={<RequestAccessPage />} />
        <Route path="/complete-registration" element={<CompleteRegistrationPage />} />
        <Route path="/verify-tenant" element={<VerifyTenantPage />} />
        <Route path="/forgot-password" element={<RequestPasswordResetPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/auth/sso/callback" element={<SSOCallbackPage />} />

        {/* Protected routes - Main application */}
        <Route
          path="/app/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Unauthorized page */}
        <Route path="/app/unauthorized" element={<UnauthorizedPage />} />

        {/* Phase 5: User Management */}
        <Route
          path="/app/users"
          element={
            <ProtectedRoute requiredPermission="iam:GetUser">
              <UsersPage />
            </ProtectedRoute>
          }
        />

        {/* Group Management */}
        <Route
          path="/app/groups"
          element={
            <ProtectedRoute requiredPermission="iam:GetUser">
              <GroupsPage />
            </ProtectedRoute>
          }
        />

        {/* Role Management */}
        <Route
          path="/app/roles"
          element={
            <ProtectedRoute requiredPermission="iam:GetUser">
              <RolesPage />
            </ProtectedRoute>
          }
        />

        {/* Service Account Management */}
        <Route
          path="/app/service-accounts"
          element={
            <ProtectedRoute requiredPermission="iam:GetUser">
              <ServiceAccountsPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 6: Application Management */}
        <Route
          path="/app/applications"
          element={
            <ProtectedRoute>
              <ApplicationsPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 7: Volume Management */}
        <Route
          path="/app/volumes"
          element={
            <ProtectedRoute>
              <VolumesPage />
            </ProtectedRoute>
          }
        />

        {/* Database Connection Management */}
        <Route
          path="/app/database-connections"
          element={
            <ProtectedRoute>
              <DatabaseConnectionsPage />
            </ProtectedRoute>
          }
        />

        {/* Secrets Management */}
        <Route
          path="/app/secrets"
          element={
            <ProtectedRoute>
              <SecretsPage />
            </ProtectedRoute>
          }
        />

        {/* Document Databases Management */}
        <Route
          path="/app/document-databases"
          element={
            <ProtectedRoute>
              <DocumentDatabasesPage />
            </ProtectedRoute>
          }
        />

        {/* Managed SQL Databases Management */}
        <Route
          path="/app/managed-sql-databases"
          element={
            <ProtectedRoute requiredPermission="resources:volume:read">
              <ManagedSqlDatabasesPage />
            </ProtectedRoute>
          }
        />

        {/* Data Agents */}
        <Route
          path="/app/data-agents"
          element={
            <ProtectedRoute requiredPermission="elt:DescribeDataAgent">
              <DataAgentsPage />
            </ProtectedRoute>
          }
        />

        {/* ELT Jobs */}
        <Route
          path="/app/elt-jobs"
          element={
            <ProtectedRoute requiredPermission="elt:DescribeJob">
              <EltJobsPage />
            </ProtectedRoute>
          }
        />

        {/* Job Executions */}
        <Route
          path="/app/job-executions"
          element={
            <ProtectedRoute requiredPermission="elt:GetEltJobExecution">
              <EltJobExecutionsPage />
            </ProtectedRoute>
          }
        />

        {/* Background Jobs */}
        <Route
          path="/app/background-jobs"
          element={
            <ProtectedRoute requiredPermission="applications:application:read">
              <BackgroundJobsPage />
            </ProtectedRoute>
          }
        />

        {/* App Jobs */}
        <Route
          path="/app/app-jobs"
          element={
            <ProtectedRoute requiredPermission="applications:application:read">
              <AppJobsPage />
            </ProtectedRoute>
          }
        />

        {/* Phase 8: Application Launcher */}
        <Route
          path="/app/launcher"
          element={
            <ProtectedRoute>
              <LauncherPage />
            </ProtectedRoute>
          }
        />

        {/* Application Launch (iframe) */}
        <Route
          path="/app/launch/:applicationId"
          element={
            <ProtectedRoute>
              <LaunchPage />
            </ProtectedRoute>
          }
        />

        {/* User Profile */}
        <Route
          path="/app/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Tenant Settings */}
        <Route
          path="/app/settings"
          element={
            <ProtectedRoute requiredPermission="tenant:UpdateSettings">
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* SSO Configuration */}
        <Route
          path="/app/settings/sso"
          element={
            <ProtectedRoute requiredPermission="sso:DescribeProvider">
              <SSOConfigPage />
            </ProtectedRoute>
          }
        />

        {/* Audit Logs */}
        <Route
          path="/app/audit-logs"
          element={
            <ProtectedRoute requiredPermission="tenant:UpdateSettings">
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />

        {/* Billing */}
        <Route
          path="/app/billing"
          element={
            <ProtectedRoute requiredPermission="tenant:DescribeBilling">
              <BillingPage />
            </ProtectedRoute>
          }
        />

        {/* Marketplace */}
        <Route
          path="/app/marketplace"
          element={
            <ProtectedRoute>
              <MarketplacePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/marketplace/:listingId"
          element={
            <ProtectedRoute>
              <ListingDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/marketplace-installed"
          element={
            <ProtectedRoute>
              <InstalledAppsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/app/partner-dashboard"
          element={
            <ProtectedRoute requiredPermission="marketplace:listing:read">
              <PartnerDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Support Tickets */}
        <Route
          path="/app/support"
          element={
            <ProtectedRoute requiredPermission="support:DescribeTicket">
              <SupportTicketsPage />
            </ProtectedRoute>
          }
        />

        {/* Inbox / Notifications */}
        <Route
          path="/app/inbox"
          element={
            <ProtectedRoute>
              <InboxPage />
            </ProtectedRoute>
          }
        />

        {/* 404 - Not Found */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h1 className="text-6xl font-bold text-gray-900">404</h1>
                <p className="mt-4 text-xl text-gray-600">Page not found</p>
                <a href="/" className="mt-6 inline-block text-primary-600 hover:text-primary-500">
                  Go back home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
